import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CARD_ACCOUNTING_FROM,
  CARD_AMOUNT_TOLERANCE,
  CARD_SAME_DAY_REASON,
  cardInstalmentsError,
  type CardMovementResponse,
  type CardReconcileRequest,
  type CardReconcileManyResult,
  type CardRuleResponse,
  type CardShopResponse,
  type CardShopRule,
  type CardShopSubjectRequest,
  formatMoney,
  fromCents,
  type ReclassChoice,
  toCents,
} from '@k-management/shared';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import { Field } from '../../components/Field.js';
import { ReclassPicker } from '../../components/ReclassPicker.js';
import { amountSort, SortHeader, useTableSort } from '../../components/SortHeader.js';
import { apiRequest } from '../../lib/api.js';
import { formatDate } from '../../lib/format.js';
import { InstalmentDuesPicker } from './InstalmentDuesPicker.js';
import { SubjectPicker } from './SubjectPayments.js';

/** What the API offers for the rendicontazione (1.55). */
export interface AccountingOptions {
  items: ReclassChoice[];
  invoices: {
    id: string;
    number: string;
    date: string;
    subjectId: string | null;
    counterpartyName: string;
    residual: string;
    selfBilling: boolean;
    reclassCode: string | null;
  }[];
  suppliers: { id: string; name: string }[];
}

export const accountingKeys = {
  options: ['treasury', 'cards', 'accounting-options'] as const,
  rules: ['treasury', 'cards', 'rules'] as const,
};

export function useAccountingOptions() {
  return useQuery({
    queryKey: accountingKeys.options,
    queryFn: () => apiRequest<AccountingOptions>('/treasury/cards/accounting-options'),
  });
}

export type AccountingState =
  'fattura' | 'soggetto' | 'rata' | 'conto' | 'da-collegare' | 'proposta' | 'da-rendicontare';

export const ACCOUNTING_STATE_LABELS: Record<AccountingState, string> = {
  'da-rendicontare': 'Da rendicontare, senza proposta',
  proposta: 'Con una proposta',
  'da-collegare': 'Con conto, fattura da collegare',
  fattura: 'Con fattura',
  soggetto: 'Al soggetto, senza documento',
  rata: 'Con una rata',
  conto: 'Con conto',
};

export function accountingState(item: CardMovementResponse): AccountingState {
  const done = item.reconciliation;
  if (done?.kind === 'fatture') return 'fattura';
  if (done?.kind === 'soggetto') return 'soggetto';
  if (done?.kind === 'rata') return 'rata';
  // With only its item, while its supplier sends invoices (1.57) or an invoice is found (2.24).
  if (done) return item.expectsInvoice || item.proposal ? 'da-collegare' : 'conto';
  return item.proposal ? 'proposta' : 'da-rendicontare';
}

/** A proposal of invoices, to the spending to account for or with only its item (2.24). */
export function proposesInvoice(item: CardMovementResponse): boolean {
  return item.proposal?.request.kind === 'fatture';
}

/** The proposal comes from the same amount and day only: the bank does not name the supplier. */
export function sameDayProposal(item: CardMovementResponse): boolean {
  return item.proposal?.reasons.includes(CARD_SAME_DAY_REASON) ?? false;
}

/**
 * The proposals that can be confirmed together: those of an invoice always,
 * the others from CARD_ACCOUNTING_FROM (2.24); an invoice goes to one spending
 * only, the first of the list.
 */
export function confirmableProposals(
  rows: readonly CardMovementResponse[],
): CardMovementResponse[] {
  const taken = new Set<string>();
  return rows.filter((item) => {
    if (!item.proposal) return false;
    if (!proposesInvoice(item)) return item.date >= CARD_ACCOUNTING_FROM && !item.reconciliation;
    const ids = item.proposal.invoices.map((invoice) => invoice.id);
    if (ids.some((id) => taken.has(id))) return false;
    for (const id of ids) taken.add(id);
    return true;
  });
}

/** An invoice whose item differs from the one of the spending (1.57): the user chooses. */
function itemDiffers(spendingItem: string | null, invoiceItem: string | null): boolean {
  return Boolean(spendingItem) && invoiceItem !== spendingItem;
}

function itemLabel(items: readonly ReclassChoice[] | undefined, code: string | null): string {
  if (!code) return '—';
  const item = items?.find((choice) => choice.code === code);
  return item ? `${item.code} ${item.description}` : code;
}

/** How a spending is accounted for, or what is proposed, in the list. */
export function AccountingCell({
  item,
  items,
}: {
  item: CardMovementResponse;
  items: readonly ReclassChoice[] | undefined;
}) {
  const done = item.reconciliation;
  const proposal = item.proposal;
  if (done?.kind === 'soggetto') {
    // Given to a subject without a document (2.25): what is still to use on his invoices.
    return (
      <>
        <span className="pill brand">al soggetto</span> {done.subject?.name ?? ''}
        {done.reclassCode && ` · conto ${itemLabel(items, done.reclassCode)}`}
        <small className="muted block">
          {done.subject && toCents(done.subject.available) > 0
            ? `disponibili ${formatMoney(done.subject.available)}`
            : `usata su ${done.invoices.map((invoice) => invoice.number).join(', ') || 'fatture'}`}
        </small>
      </>
    );
  }
  if (done?.kind === 'rata') {
    return (
      <>
        <span className="pill brand">rata</span>{' '}
        {(done.instalments ?? []).map((due) => `${due.number} · ${due.planName}`).join(', ')}
        {done.reclassCode && ` · conto ${itemLabel(items, done.reclassCode)}`}
      </>
    );
  }
  if (done) {
    return done.kind === 'fatture' ? (
      <>
        <span className="pill ok">fattura</span>{' '}
        {done.invoices.map((invoice) => invoice.number).join(', ')}
        {done.reclassCode && ` · conto ${itemLabel(items, done.reclassCode)}`}
        <small className="muted block">{done.invoices[0]?.counterpartyName}</small>
      </>
    ) : (
      <>
        <span className={`pill ${item.expectsInvoice || proposal ? 'warn' : 'ok'}`}>
          {item.expectsInvoice || proposal ? 'conto, fattura da collegare' : 'conto'}
        </span>{' '}
        {itemLabel(items, done.reclassCode)}
        {proposal && (
          <small className="muted block">
            fattura proposta {proposal.invoices.map((invoice) => invoice.number).join(', ')} ·{' '}
            {proposal.invoices[0]?.counterpartyName} ({proposal.reasons.join(' · ')})
          </small>
        )}
        {done.note && <small className="muted block">{done.note}</small>}
      </>
    );
  }
  if (!proposal) return <span className="pill warn">da rendicontare</span>;
  return (
    <>
      <span className="pill brand">proposta</span>{' '}
      {proposal.request.kind === 'fatture'
        ? `fattura ${proposal.invoices.map((invoice) => invoice.number).join(', ')} · ${proposal.invoices[0]?.counterpartyName ?? ''}`
        : `conto ${itemLabel(items, proposal.request.reclassCode)}`}
      <small className="muted block">{proposal.reasons.join(' · ')}</small>
    </>
  );
}

/** The proposals of the list confirmed together, after a question. */
export function ConfirmProposals({
  rows,
  onClose,
}: {
  rows: CardMovementResponse[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  // The invoices whose item differs from that of the spending: the user chooses (1.57).
  const differing = rows.filter((item) => {
    const request = item.proposal!.request;
    return (
      request.kind === 'fatture' &&
      item.proposal!.invoices.some((invoice) =>
        itemDiffers(request.reclassCode ?? null, invoice.reclassCode),
      )
    );
  });
  const [toInvoices, setToInvoices] = useState(false);
  // The shop of a proposal of the same day becomes a rule of its supplier (2.24).
  const sameDay = rows.filter(sameDayProposal).length;
  const [remember, setRemember] = useState(true);
  const confirm = useMutation({
    mutationFn: () =>
      apiRequest<{ done: number; failed: { movementId: string; error: string }[] }>(
        '/treasury/cards/movements/reconcile',
        {
          method: 'POST',
          body: {
            items: rows.map((item) => {
              const request = item.proposal!.request;
              return {
                movementId: item.id,
                request:
                  request.kind === 'fatture'
                    ? {
                        ...request,
                        invoiceReclass: toInvoices,
                        remember: remember && sameDayProposal(item),
                      }
                    : request,
              };
            }),
          },
        },
      ),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['treasury'] });
      if (!result.failed.length) onClose();
    },
  });
  const invoices = rows.filter((item) => item.proposal!.request.kind === 'fatture').length;
  return (
    <ConfirmDialog
      title="Confermare le proposte?"
      confirmLabel={`Conferma ${rows.length}`}
      busy={confirm.isPending}
      error={confirm.error?.message}
      onConfirm={() => confirm.mutate()}
      onCancel={onClose}
    >
      <p>
        Si rendicontano {rows.length} spese come proposto: {invoices} con la fattura (che risulta
        pagata con la carta), {rows.length - invoices} con il conto. Le spese che hanno già il conto
        lo tengono. Si possono annullare una per una.
      </p>
      {sameDay > 0 && (
        <label className="checkbox">
          <input
            type="checkbox"
            checked={remember}
            onChange={(event) => setRemember(event.target.checked)}
          />
          Ricorda il fornitore delle {sameDay} spese trovate per stesso importo e stessa data: le
          prossime spese di quei negozi cercano le sue fatture
        </label>
      )}
      {differing.length > 0 && (
        <div className="notice warn stack">
          <span>
            {differing.length === 1 ? '1 fattura ha' : `${differing.length} fatture hanno`} una voce
            riclassificata diversa dal conto della spesa (o nessuna).
          </span>
          <label className="checkbox">
            <input
              type="radio"
              name="invoice-item"
              checked={!toInvoices}
              onChange={() => setToInvoices(false)}
            />
            Tieni la voce delle fatture
          </label>
          <label className="checkbox">
            <input
              type="radio"
              name="invoice-item"
              checked={toInvoices}
              onChange={() => setToInvoices(true)}
            />
            Metti sulle fatture il conto della spesa
          </label>
        </div>
      )}
      {confirm.data && confirm.data.failed.length > 0 && (
        <p className="error">
          {confirm.data.done} fatte; {confirm.data.failed.length} no:{' '}
          {[...new Set(confirm.data.failed.map((item) => item.error))].join('; ')}
        </p>
      )}
    </ConfirmDialog>
  );
}

/**
 * A spending accounted for: with the received invoices it pays or with an
 * item of the plan Base; the choice can be remembered for the shop.
 */
export function AccountingDialog({
  item,
  canWrite,
  onClose,
}: {
  item: CardMovementResponse;
  canWrite: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const options = useAccountingOptions();
  const proposal = item.proposal?.request;
  const proposedInvoice = proposal?.kind === 'fatture' ? item.proposal!.invoices[0] : undefined;
  const refund = toCents(item.amount) < 0;
  const [kind, setKind] = useState<'fatture' | 'conto' | 'soggetto' | 'rata'>(
    proposal?.kind ?? (refund ? 'conto' : 'fatture'),
  );
  // «Al soggetto, senza documento» (2.25): the top-up of a prepaid credit.
  const [subject, setSubject] = useState<{ id: string; name: string } | null>(null);
  const [supplier, setSupplier] = useState<string>('');
  const [allocations, setAllocations] = useState<Record<string, string>>(
    proposal?.kind === 'fatture'
      ? Object.fromEntries(proposal.allocations.map((line) => [line.invoiceId, line.amount]))
      : {},
  );
  const accounted = item.reconciliation;
  // The item of the spending: proposed, or the one it has; it stays with the invoices (1.57).
  const [reclassCode, setReclassCode] = useState<string | null>(
    accounted?.reclassCode ?? proposal?.reclassCode ?? null,
  );
  const [note, setNote] = useState('');
  // Found by amount and day only: the shop is remembered with its supplier, unless unticked (2.24).
  const [remember, setRemember] = useState(sameDayProposal(item));
  // A spending with only its item: its invoice is linked keeping the item.
  const [linking, setLinking] = useState(false);
  // The same with its instalment (3.21).
  const [linkingDue, setLinkingDue] = useState(false);
  const [dues, setDues] = useState<{ ids: string[]; cents: number }>({ ids: [], cents: 0 });
  const chooseDues = useCallback((ids: string[], cents: number) => setDues({ ids, cents }), []);
  const [invoiceReclass, setInvoiceReclass] = useState(false);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  // The supplier of the proposed invoice, once the invoices are there.
  useEffect(() => {
    if (supplier || !options.data || !proposedInvoice) return;
    const found = options.data.invoices.find((invoice) => invoice.id === proposedInvoice.id);
    if (found?.subjectId) setSupplier(found.subjectId);
  }, [options.data, proposedInvoice, supplier]);
  const done = async () => {
    await queryClient.invalidateQueries({ queryKey: ['treasury'] });
    onClose();
  };
  const save = useMutation({
    mutationFn: (body: CardReconcileRequest) =>
      apiRequest<void>(`/treasury/cards/movements/${item.id}/reconciliation`, {
        method: 'PUT',
        body,
      }),
    onSuccess: done,
  });
  const undo = useMutation({
    mutationFn: () =>
      apiRequest<void>(`/treasury/cards/movements/${item.id}/reconciliation`, {
        method: 'DELETE',
      }),
    onSuccess: done,
  });
  const noInvoice = useMutation({
    mutationFn: (value: boolean) =>
      apiRequest<void>(`/treasury/cards/movements/${item.id}/no-invoice`, {
        method: 'PUT',
        body: { noInvoice: value },
      }),
    onSuccess: done,
  });
  const open = (options.data?.invoices ?? []).filter(
    (invoice) => supplier && invoice.subjectId === supplier,
  );
  const openSorting = useTableSort('cards.accounting.invoices', open, {
    number: (invoice) => invoice.number,
    date: (invoice) => invoice.date,
    residual: (invoice) => amountSort(invoice.residual),
  });
  const chosen = Object.entries(allocations).filter(([, value]) => value && toCents(value) > 0);
  const total = chosen.reduce((sum, [, value]) => sum + toCents(value), 0);
  const limit = toCents(item.amount) + Math.round(toCents(item.amount) * CARD_AMOUNT_TOLERANCE);
  const dueMode = kind === 'rata';
  const invoiceMode = !dueMode && (linking || kind === 'fatture');
  const dueProblem = dues.ids.length
    ? cardInstalmentsError(item.amount, [{ amount: fromCents(dues.cents) }])
    : null;
  const body: CardReconcileRequest | null = dueMode
    ? dues.ids.length && !dueProblem && reclassCode
      ? { kind: 'rata', dueIds: dues.ids, reclassCode }
      : null
    : kind === 'soggetto' && !linking
      ? subject
        ? { kind: 'soggetto', subjectId: subject.id, note: note.trim() || null }
        : null
      : invoiceMode
        ? chosen.length && total <= limit
          ? {
              kind: 'fatture',
              allocations: chosen.map(([invoiceId, amount]) => ({
                invoiceId,
                amount: fromCents(toCents(amount)),
              })),
              remember,
              reclassCode,
              invoiceReclass,
            }
          : null
        : reclassCode
          ? { kind: 'conto', reclassCode, note: note.trim() || null, remember }
          : null;
  const differing = (options.data?.invoices ?? []).filter(
    (invoice) => invoice.id in allocations && itemDiffers(reclassCode, invoice.reclassCode),
  );

  return (
    <dialog ref={dialogRef} className="dialog" onClose={onClose}>
      <div className="stack">
        <h2>Rendicontare la spesa</h2>
        <p>
          <strong>
            {formatDate(item.date)} · {item.description} · {formatMoney(item.amount)}
          </strong>
          <small className="muted block">
            carta •••• {item.last4} · {item.holder}
            {item.category ? ` · ${item.category}` : ''}
            {item.currency !== 'EUR' && ` · ${item.originalAmount} ${item.currency}`}
          </small>
        </p>
        {accounted && !linking && !linkingDue ? (
          <>
            <div className="notice info">
              {accounted.kind === 'rata' ? (
                <>
                  Paga{' '}
                  {(accounted.instalments ?? [])
                    .map(
                      (due) =>
                        `la rata ${due.number} di «${due.planName}» (${formatMoney(due.amount)})`,
                    )
                    .join(', ')}
                  {accounted.reclassCode &&
                    ` · conto ${itemLabel(options.data?.items, accounted.reclassCode)}`}
                  .
                </>
              ) : accounted.kind === 'soggetto' ? (
                <>
                  Pagata a {accounted.subject?.name ?? 'un soggetto'} senza documento
                  {accounted.subject && `: disponibili ${formatMoney(accounted.subject.available)}`}
                  {accounted.invoices.length > 0 &&
                    `, usata su ${accounted.invoices.map((invoice) => invoice.number).join(', ')}`}
                  {accounted.reclassCode &&
                    ` · conto ${itemLabel(options.data?.items, accounted.reclassCode)}`}
                  . Si usa sulle sue fatture con «Usa» o «Riabbina in ordine di data».
                </>
              ) : accounted.kind === 'fatture' ? (
                <>
                  Paga{' '}
                  {accounted.invoices
                    .map(
                      (invoice) =>
                        `${invoice.number} di ${invoice.counterpartyName} (${formatMoney(invoice.amount)})`,
                    )
                    .join(', ')}
                  {accounted.reclassCode &&
                    ` · conto ${itemLabel(options.data?.items, accounted.reclassCode)}`}
                  .
                </>
              ) : (
                <>
                  Conto {itemLabel(options.data?.items, accounted.reclassCode)}
                  {accounted.note && ` — ${accounted.note}`}.
                  {accounted.noInvoice && ' Senza fattura: non ne arriverà.'}
                  {item.proposal
                    ? ` Trovata la fattura ${item.proposal.invoices.map((invoice) => invoice.number).join(', ')} di ${item.proposal.invoices[0]?.counterpartyName} (${item.proposal.reasons.join(' · ')}): va ancora collegata.`
                    : item.expectsInvoice && ' Il fornitore manda fattura: va ancora collegata.'}
                </>
              )}
            </div>
            {(undo.error ?? noInvoice.error) && (
              <p className="error">{(undo.error ?? noInvoice.error)?.message}</p>
            )}
            <div className="actions">
              <button type="button" onClick={onClose}>
                Chiudi
              </button>
              {canWrite && (
                <button type="button" onClick={() => undo.mutate()} disabled={undo.isPending}>
                  {undo.isPending
                    ? 'Attendere…'
                    : accounted.kind !== 'conto' && accounted.reclassCode
                      ? accounted.kind === 'soggetto'
                        ? 'Togli dal soggetto (il conto resta)'
                        : accounted.kind === 'rata'
                          ? 'Scollega la rata (il conto resta)'
                          : 'Scollega la fattura (il conto resta)'
                      : 'Annulla la rendicontazione'}
                </button>
              )}
              {canWrite &&
                accounted.kind === 'conto' &&
                (item.expectsInvoice || item.proposal || accounted.noInvoice) && (
                  <button
                    type="button"
                    disabled={noInvoice.isPending}
                    onClick={() => noInvoice.mutate(!accounted.noInvoice)}
                  >
                    {noInvoice.isPending
                      ? 'Attendere…'
                      : accounted.noInvoice
                        ? 'Aspetta la fattura'
                        : 'Non arriverà fattura'}
                  </button>
                )}
              {canWrite && accounted.kind === 'conto' && !refund && (
                <button
                  type="button"
                  onClick={() => {
                    setKind('rata');
                    setLinkingDue(true);
                  }}
                >
                  Collega la rata
                </button>
              )}
              {canWrite && accounted.kind === 'conto' && !refund && (
                <button type="button" className="primary" onClick={() => setLinking(true)}>
                  Collega la fattura
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            {item.proposal && (
              <p className="muted small">Proposta: {item.proposal.reasons.join(' · ')}.</p>
            )}
            {linking || linkingDue ? (
              <p className="muted small">
                Il conto {itemLabel(options.data?.items, reclassCode)} resta sulla spesa.
              </p>
            ) : (
              <div className="segmented-small" role="group" aria-label="Come">
                <button
                  type="button"
                  className={kind === 'fatture' ? 'selected' : ''}
                  disabled={refund}
                  title={refund ? 'Un rimborso si rendiconta con un conto' : undefined}
                  onClick={() => setKind('fatture')}
                >
                  Fattura ricevuta
                </button>
                <button
                  type="button"
                  className={kind === 'conto' ? 'selected' : ''}
                  onClick={() => setKind('conto')}
                >
                  Senza fattura: conto
                </button>
                <button
                  type="button"
                  className={kind === 'soggetto' ? 'selected' : ''}
                  disabled={refund}
                  onClick={() => setKind('soggetto')}
                >
                  Al soggetto, senza documento
                </button>
                <button
                  type="button"
                  className={kind === 'rata' ? 'selected' : ''}
                  disabled={refund}
                  onClick={() => setKind('rata')}
                >
                  Con una rata
                </button>
              </div>
            )}
            {kind === 'soggetto' && !linking && (
              <>
                <p className="muted small">
                  Per un credito prepagato (una ricarica): la spesa va nella sezione «Finanziaria»
                  del soggetto e si usa sulle sue fatture quando arrivano.
                </p>
                <Field label="Soggetto">
                  <SubjectPicker value={subject} onChange={setSubject} />
                </Field>
                <Field label="Nota (facoltativa)">
                  <input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
                </Field>
              </>
            )}
            {dueMode && (
              <>
                <InstalmentDuesPicker movement={item} card onChange={chooseDues} />
                {dueProblem && <p className="error">{dueProblem}.</p>}
                {dues.ids.length > 0 && !dueProblem && dues.cents !== toCents(item.amount) && (
                  <p className="muted small">
                    Rate {formatMoney(fromCents(dues.cents))} + commissione{' '}
                    {formatMoney(fromCents(toCents(item.amount) - dues.cents))}.
                  </p>
                )}
                {!linkingDue && options.data && (
                  <Field label="Conto della spesa">
                    <ReclassPicker
                      value={reclassCode}
                      choices={options.data.items}
                      onChange={setReclassCode}
                      noneLabel="— scegli"
                    />
                  </Field>
                )}
              </>
            )}
            {options.isPending && <p className="muted">Caricamento…</p>}
            {options.data && invoiceMode && kind !== 'soggetto' && (
              <>
                <Field label="Fornitore">
                  <select value={supplier} onChange={(event) => setSupplier(event.target.value)}>
                    <option value="">— scegli —</option>
                    {options.data.suppliers.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.name}
                      </option>
                    ))}
                  </select>
                </Field>
                {supplier && open.length === 0 && (
                  <p className="muted small">Nessuna fattura aperta di questo fornitore.</p>
                )}
                {open.length > 0 && (
                  <div className="table-scroll">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th />
                          <SortHeader sort={openSorting} column="number">
                            Fattura
                          </SortHeader>
                          <SortHeader sort={openSorting} column="date">
                            Data
                          </SortHeader>
                          <SortHeader sort={openSorting} column="residual" numeric>
                            Da pagare
                          </SortHeader>
                          <th className="numeric">Pagata con la spesa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {openSorting.rows.map((invoice) => {
                          const checked = invoice.id in allocations;
                          return (
                            <tr key={invoice.id}>
                              <td>
                                <input
                                  type="checkbox"
                                  aria-label={`Fattura ${invoice.number}`}
                                  checked={checked}
                                  onChange={(event) => {
                                    const next = { ...allocations };
                                    if (event.target.checked) {
                                      const left = Math.max(0, toCents(item.amount) - total);
                                      next[invoice.id] = fromCents(
                                        Math.min(
                                          toCents(invoice.residual),
                                          left || toCents(invoice.residual),
                                        ),
                                      );
                                    } else delete next[invoice.id];
                                    setAllocations(next);
                                  }}
                                />
                              </td>
                              <td>
                                {invoice.number}
                                {invoice.selfBilling && (
                                  <small className="muted block">autofattura</small>
                                )}
                              </td>
                              <td>{formatDate(invoice.date)}</td>
                              <td className="numeric">{formatMoney(invoice.residual)}</td>
                              <td className="numeric">
                                {checked && (
                                  <input
                                    className="amount-input"
                                    inputMode="decimal"
                                    value={allocations[invoice.id]}
                                    onChange={(event) =>
                                      setAllocations({
                                        ...allocations,
                                        [invoice.id]: event.target.value.replace(',', '.'),
                                      })
                                    }
                                  />
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {chosen.length > 0 && total > limit && (
                  <p className="error">
                    Le fatture scelte superano la spesa di più del 2% (il margine per il cambio).
                  </p>
                )}
                {chosen.length > 0 && total !== toCents(item.amount) && total <= limit && (
                  <p className="muted small">
                    Differenza con la spesa: {formatMoney(fromCents(total - toCents(item.amount)))}
                    {total > toCents(item.amount) ? ', costo del cambio.' : '.'}
                  </p>
                )}
                {!linking && (
                  <Field label="Conto della spesa (facoltativo)">
                    <ReclassPicker
                      value={reclassCode}
                      choices={options.data.items}
                      onChange={setReclassCode}
                      noneLabel="— nessuno: il costo è quello della fattura"
                    />
                  </Field>
                )}
                {differing.length > 0 && (
                  <div className="notice warn stack">
                    <span>
                      {differing
                        .map(
                          (invoice) =>
                            `La fattura ${invoice.number} ha ${invoice.reclassCode ? `la voce ${itemLabel(options.data!.items, invoice.reclassCode)}` : 'nessuna voce'}`,
                        )
                        .join('; ')}
                      , la spesa il conto {itemLabel(options.data.items, reclassCode)}.
                    </span>
                    <label className="checkbox">
                      <input
                        type="radio"
                        name="invoice-item"
                        checked={!invoiceReclass}
                        onChange={() => setInvoiceReclass(false)}
                      />
                      Tieni la voce della fattura
                    </label>
                    <label className="checkbox">
                      <input
                        type="radio"
                        name="invoice-item"
                        checked={invoiceReclass}
                        onChange={() => setInvoiceReclass(true)}
                      />
                      Metti sulla fattura il conto della spesa
                    </label>
                  </div>
                )}
              </>
            )}
            {options.data && !invoiceMode && kind === 'conto' && (
              <>
                <Field label="Conto del piano Base">
                  <ReclassPicker
                    value={reclassCode}
                    choices={options.data.items}
                    onChange={setReclassCode}
                    noneLabel="— scegli"
                  />
                </Field>
                <Field label="Nota (facoltativa)">
                  <input value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} />
                </Field>
              </>
            )}
            {!dueMode && (kind !== 'soggetto' || linking) && (
              <label className="checkbox">
                <input
                  type="checkbox"
                  checked={remember}
                  onChange={(event) => setRemember(event.target.checked)}
                />
                Ricorda per le prossime spese di «{item.description}»:{' '}
                {invoiceMode ? 'si cercano le fatture di questo fornitore' : 'questo conto'}
              </label>
            )}
            {save.error && <p className="error">{save.error.message}</p>}
            <div className="actions">
              <button type="button" onClick={onClose} disabled={save.isPending}>
                Annulla
              </button>
              {canWrite && (
                <button
                  type="button"
                  className="primary"
                  disabled={!body || save.isPending}
                  onClick={() => body && save.mutate(body)}
                >
                  {save.isPending ? 'Attendere…' : 'Salva'}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </dialog>
  );
}

/**
 * «Regole» (1.55): what the rendicontazione remembers, by shop and by
 * category; the rule of the shop comes first.
 */
export function RulesTab({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient();
  const rules = useQuery({
    queryKey: accountingKeys.rules,
    queryFn: () => apiRequest<CardRuleResponse[]>('/treasury/cards/rules'),
  });
  const options = useAccountingOptions();
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['treasury', 'cards'] });
  const save = useMutation({
    mutationFn: (rule: CardRuleResponse & { reclassCode: string | null }) =>
      apiRequest<void>('/treasury/cards/rules', {
        method: 'PUT',
        body: {
          kind: rule.kind,
          key: rule.key,
          subjectId: rule.subjectId,
          reclassCode: rule.reclassCode,
        },
      }),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) =>
      apiRequest<void>(`/treasury/cards/rules/${id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  });
  // What «Allora» shows of a rule of a shop.
  const ruleAction = (rule: CardRuleResponse) =>
    [
      rule.reclassCode && `conto ${itemLabel(options.data?.items, rule.reclassCode)}`,
      rule.subjectName && `fatture di ${rule.subjectName}`,
    ]
      .filter(Boolean)
      .join(' · ');
  const shops = (rules.data ?? []).filter((rule) => rule.kind === 'fornitore');
  const categories = (rules.data ?? []).filter((rule) => rule.kind === 'categoria');
  const shopSorting = useTableSort('cards.rules.shops', shops, {
    label: (rule) => rule.label,
    action: (rule) => ruleAction(rule),
    spending: (rule) => rule.spending,
  });
  const categorySorting = useTableSort('cards.rules.categories', categories, {
    label: (rule) => rule.label,
    spending: (rule) => rule.spending,
  });
  if (rules.isError) return <p className="error">{rules.error.message}</p>;
  if (!rules.data) return <p className="muted">Caricamento…</p>;
  const error = save.error ?? remove.error;

  return (
    <>
      <p className="muted small">
        Le regole dei fornitori nascono con «Ricorda» quando si rendiconta una spesa; quelle delle
        categorie propongono un conto alle spese senza regola del fornitore. Sono solo proposte: si
        confermano sempre.
      </p>
      {error && <p className="error">{error.message}</p>}
      <h3>Per fornitore</h3>
      {shops.length === 0 ? (
        <p className="muted">Nessuna regola: si creano con «Ricorda».</p>
      ) : (
        <div className="table-scroll">
          <table className="data-table rules-table">
            <thead>
              <tr>
                <SortHeader sort={shopSorting} column="label">
                  Quando la descrizione è
                </SortHeader>
                <SortHeader sort={shopSorting} column="action">
                  Allora
                </SortHeader>
                <SortHeader sort={shopSorting} column="spending" numeric>
                  Spese
                </SortHeader>
                <th />
              </tr>
            </thead>
            <tbody>
              {shopSorting.rows.map((rule) => (
                <tr key={rule.id}>
                  <td>{rule.label}</td>
                  <td>{ruleAction(rule)}</td>
                  <td className="numeric">{rule.spending}</td>
                  <td>
                    {canWrite && (
                      <button
                        type="button"
                        className="link"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(rule.id)}
                      >
                        Togli
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <h3>Per categoria</h3>
      <div className="table-scroll">
        <table className="data-table rules-table">
          <thead>
            <tr>
              <SortHeader sort={categorySorting} column="label">
                Categoria della banca
              </SortHeader>
              <th>Conto proposto</th>
              <SortHeader sort={categorySorting} column="spending" numeric>
                Spese
              </SortHeader>
            </tr>
          </thead>
          <tbody>
            {categorySorting.rows.map((rule) => (
              <tr key={rule.id}>
                <td>
                  {rule.label} <small className="muted">({rule.key})</small>
                </td>
                <td className="rule-picker">
                  {options.data ? (
                    <ReclassPicker
                      value={rule.reclassCode}
                      choices={options.data.items}
                      disabled={!canWrite || save.isPending}
                      noneLabel="— scegli"
                      onChange={(code) => code && save.mutate({ ...rule, reclassCode: code })}
                    />
                  ) : (
                    rule.reclassCode
                  )}
                </td>
                <td className="numeric">{rule.spending}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

/** «Classificato come» of a shop in the summary by supplier (1.56). */
export function ShopRuleCell({
  rule,
  items,
}: {
  rule: CardShopRule | null;
  items: readonly ReclassChoice[] | undefined;
}) {
  if (!rule) return <span className="pill muted">nessuna regola</span>;
  // The item and the supplier of the invoices live together (1.57).
  return (
    <>
      {rule.reclassCode && (
        <span className="pill ok">conto {itemLabel(items, rule.reclassCode)}</span>
      )}{' '}
      {rule.subjectId && (
        <span className="pill brand">fatture di {rule.subjectName ?? 'fornitore'}</span>
      )}
    </>
  );
}

/**
 * The panel of a shop (1.56, decision of 10/10/2026): all its spending, of
 * every year, classified at once with an item of the plan Base (its rule,
 * given to the spending still open) or with the invoices of a supplier.
 */
export function ShopPanel({
  shopKey,
  canWrite,
  onClose,
}: {
  shopKey: string;
  canWrite: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const options = useAccountingOptions();
  const shop = useQuery({
    queryKey: ['treasury', 'cards', 'shop', shopKey],
    queryFn: () =>
      apiRequest<CardShopResponse>(`/treasury/cards/shop?key=${encodeURIComponent(shopKey)}`),
  });
  const [reclassCode, setReclassCode] = useState<string | null>(null);
  const [apply, setApply] = useState(true);
  const [supplier, setSupplier] = useState('');
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [opened, setOpened] = useState<CardMovementResponse | null>(null);
  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);
  // The choices start from the rule of the shop.
  const rule = shop.data?.rule ?? null;
  // Without a rule, the item most proposed to its spending (from the categories).
  const proposed = (shop.data?.items ?? [])
    .map((item) =>
      item.proposal?.request.kind === 'conto' ? item.proposal.request.reclassCode : null,
    )
    .filter((code): code is string => code !== null);
  const usual = proposed.length
    ? proposed.reduce(
        (best, code) =>
          proposed.filter((item) => item === code).length >
          proposed.filter((item) => item === best).length
            ? code
            : best,
        proposed[0]!,
      )
    : null;
  useEffect(() => {
    if (!shop.data) return;
    if (rule?.reclassCode || usual) {
      setReclassCode((current) => current ?? rule?.reclassCode ?? usual);
    }
    if (rule?.subjectId) setSupplier((current) => current || rule.subjectId!);
  }, [shop.data, rule, usual]);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['treasury'] });
  const giveItem = useMutation({
    mutationFn: () =>
      apiRequest<CardReconcileManyResult>('/treasury/cards/shop/item', {
        method: 'POST',
        body: { key: shopKey, reclassCode, apply },
      }),
    onSuccess: refresh,
  });
  const saveSupplier = useMutation({
    mutationFn: () =>
      apiRequest<void>('/treasury/cards/rules', {
        method: 'PUT',
        // The item of the rule stays (1.57).
        body: {
          kind: 'fornitore',
          key: shopKey,
          subjectId: supplier,
          reclassCode: rule?.reclassCode ?? null,
        },
      }),
    onSuccess: refresh,
  });
  const items = shop.data?.items ?? [];
  const itemSorting = useTableSort('cards.shop.spending', items, {
    date: (item) => item.date,
    card: (item) => item.last4,
    description: (item) => item.description,
    amount: (item) => amountSort(item.amount),
  });
  // The invoices found, also for the spending that has its item already (1.57).
  const found = items.filter(
    (item) =>
      (!item.reconciliation || item.reconciliation.kind === 'conto') &&
      item.proposal?.request.kind === 'fatture',
  );
  const chosen = found.filter((item) => !skipped.has(item.id));
  const differs = (item: CardMovementResponse) => {
    const request = item.proposal!.request;
    return (
      request.kind === 'fatture' &&
      item.proposal!.invoices.some((invoice) =>
        itemDiffers(request.reclassCode ?? null, invoice.reclassCode),
      )
    );
  };
  const [toInvoice, setToInvoice] = useState<Set<string>>(new Set());
  const withoutItem = items.filter(
    (item) =>
      !item.reconciliation ||
      (item.reconciliation.kind === 'fatture' && !item.reconciliation.reclassCode),
  ).length;
  const confirm = useMutation({
    mutationFn: () =>
      apiRequest<CardReconcileManyResult>('/treasury/cards/movements/reconcile', {
        method: 'POST',
        body: {
          items: chosen.map((item) => {
            const request = item.proposal!.request;
            return {
              movementId: item.id,
              request:
                request.kind === 'fatture'
                  ? { ...request, invoiceReclass: toInvoice.has(item.id) }
                  : request,
            };
          }),
        },
      }),
    onSuccess: refresh,
  });
  // A prepaid credit (2.25): the top-ups go to the subject without a document.
  const [prepaidTo, setPrepaidTo] = useState<{ id: string; name: string } | null>(null);
  const toGive = items.filter(
    (item) =>
      toCents(item.amount) > 0 && (!item.reconciliation || item.reconciliation.kind === 'conto'),
  );
  const givePrepaid = useMutation({
    mutationFn: () =>
      apiRequest<CardReconcileManyResult>('/treasury/cards/shop/subject', {
        method: 'POST',
        body: { key: shopKey, subjectId: prepaidTo!.id } satisfies CardShopSubjectRequest,
      }),
    onSuccess: refresh,
  });
  const error = giveItem.error ?? saveSupplier.error ?? confirm.error ?? givePrepaid.error;
  const supplierSaved = rule?.subjectId === supplier && supplier !== '';
  const result = giveItem.data ?? confirm.data ?? givePrepaid.data;

  return (
    <dialog ref={dialogRef} className="dialog wide-dialog" onClose={onClose}>
      <div className="stack">
        <h2>{shop.data?.label ?? 'Fornitore'}</h2>
        {shop.isError && <p className="error">{shop.error.message}</p>}
        {shop.data && (
          <>
            <p className="muted">
              {items.length} spese di tutti gli anni · {formatMoney(shop.data.total)} ·{' '}
              {shop.data.open === 0 ? (
                <span className="pill ok">tutte rendicontate</span>
              ) : (
                <span className="pill warn">{shop.data.open} da rendicontare</span>
              )}{' '}
              · regola: <ShopRuleCell rule={rule} items={options.data?.items} />
            </p>
            {canWrite && (
              <div className="shop-options">
                <div className="card stack">
                  <strong>Senza fattura: conto del riclassificato</strong>
                  {options.data && (
                    <ReclassPicker
                      value={reclassCode}
                      choices={options.data.items}
                      onChange={setReclassCode}
                      noneLabel="— scegli"
                    />
                  )}
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={apply}
                      onChange={(event) => setApply(event.target.checked)}
                    />
                    Applica alle {withoutItem} spese senza conto (anche a quelle già con la fattura)
                  </label>
                  <small className="muted">
                    Diventa la regola del fornitore: anche le spese che arriveranno lo hanno come
                    proposta. Le spese già rendicontate non cambiano.
                  </small>
                  <div>
                    <button
                      type="button"
                      className="primary"
                      disabled={!reclassCode || giveItem.isPending}
                      onClick={() => giveItem.mutate()}
                    >
                      {giveItem.isPending
                        ? 'Attendere…'
                        : apply
                          ? 'Salva e applica'
                          : 'Salva la regola'}
                    </button>
                  </div>
                </div>
                <div className="card stack">
                  <strong>Con fattura: fornitore dell’anagrafica</strong>
                  {options.data && (
                    <select value={supplier} onChange={(event) => setSupplier(event.target.value)}>
                      <option value="">— scegli —</option>
                      {options.data.suppliers.map((entry) => (
                        <option key={entry.id} value={entry.id}>
                          {entry.name}
                        </option>
                      ))}
                    </select>
                  )}
                  {!supplierSaved ? (
                    <div>
                      <button
                        type="button"
                        className="primary"
                        disabled={!supplier || saveSupplier.isPending}
                        onClick={() => saveSupplier.mutate()}
                      >
                        {saveSupplier.isPending
                          ? 'Attendere…'
                          : 'Salva il fornitore e cerca le fatture'}
                      </button>
                    </div>
                  ) : found.length === 0 ? (
                    <small className="muted">
                      Nessuna fattura aperta di questo fornitore per le spese da rendicontare
                      (stesso importo, o entro il 2% per il cambio).
                    </small>
                  ) : (
                    <>
                      <ul className="plain-list">
                        {found.map((item) => (
                          <li key={item.id}>
                            <label className="checkbox">
                              <input
                                type="checkbox"
                                checked={!skipped.has(item.id)}
                                onChange={(event) => {
                                  const next = new Set(skipped);
                                  if (event.target.checked) next.delete(item.id);
                                  else next.add(item.id);
                                  setSkipped(next);
                                }}
                              />
                              {formatDate(item.date)} · {formatMoney(item.amount)} → fattura{' '}
                              {item.proposal!.invoices.map((invoice) => invoice.number).join(', ')}{' '}
                              ({formatMoney(item.proposal!.invoices[0]!.residual)})
                              {item.reconciliation && (
                                <span className="muted">
                                  {' '}
                                  · ha già il conto {item.reconciliation.reclassCode}
                                </span>
                              )}
                            </label>
                            {differs(item) && (
                              <label className="checkbox shop-differs">
                                <input
                                  type="checkbox"
                                  checked={toInvoice.has(item.id)}
                                  onChange={(event) => {
                                    const next = new Set(toInvoice);
                                    if (event.target.checked) next.add(item.id);
                                    else next.delete(item.id);
                                    setToInvoice(next);
                                  }}
                                />
                                La fattura ha{' '}
                                {item.proposal!.invoices[0]!.reclassCode
                                  ? `la voce ${item.proposal!.invoices[0]!.reclassCode}`
                                  : 'nessuna voce'}
                                : metti il conto{' '}
                                {item.proposal!.request.kind === 'fatture'
                                  ? item.proposal!.request.reclassCode
                                  : ''}{' '}
                                anche sulla fattura
                              </label>
                            )}
                          </li>
                        ))}
                      </ul>
                      <div>
                        <button
                          type="button"
                          className="primary"
                          disabled={!chosen.length || confirm.isPending}
                          onClick={() => confirm.mutate()}
                        >
                          {confirm.isPending ? 'Attendere…' : `Conferma ${chosen.length} fatture`}
                        </button>
                      </div>
                    </>
                  )}
                </div>
                <div className="card stack">
                  <strong>Credito prepagato: al soggetto, senza documento</strong>
                  <small className="muted">
                    Per le ricariche (EasyPark e simili): le fatture arrivano dopo, per quanto si
                    consuma. Le spese vanno nella sezione «Finanziaria» del soggetto; con «Riabbina
                    in ordine di data» nella sua scheda pagano le fatture, le più vecchie prima.
                  </small>
                  <SubjectPicker value={prepaidTo} onChange={setPrepaidTo} />
                  <div>
                    <button
                      type="button"
                      className="primary"
                      disabled={!prepaidTo || !toGive.length || givePrepaid.isPending}
                      onClick={() => givePrepaid.mutate()}
                    >
                      {givePrepaid.isPending
                        ? 'Attendere…'
                        : `Imputa ${toGive.length} spese al soggetto`}
                    </button>
                  </div>
                  <small className="muted">
                    Le spese con il conto lo tengono; quelle già con fattura restano come sono.
                  </small>
                </div>
              </div>
            )}
            {error && <p className="error">{error.message}</p>}
            {result && result.failed.length > 0 && (
              <p className="error">
                {result.done} fatte; {result.failed.length} no:{' '}
                {[...new Set(result.failed.map((item) => item.error))].join('; ')}
              </p>
            )}
            <div className="table-scroll shop-spending">
              <table className="data-table">
                <thead>
                  <tr>
                    <SortHeader sort={itemSorting} column="date">
                      Data
                    </SortHeader>
                    <SortHeader sort={itemSorting} column="card">
                      Carta
                    </SortHeader>
                    <SortHeader sort={itemSorting} column="description">
                      Descrizione
                    </SortHeader>
                    <SortHeader sort={itemSorting} column="amount" numeric>
                      Euro
                    </SortHeader>
                    <th>Rendicontazione</th>
                  </tr>
                </thead>
                <tbody>
                  {itemSorting.rows.map((item) => (
                    <tr key={item.id} className="clickable" onClick={() => setOpened(item)}>
                      <td>{formatDate(item.date)}</td>
                      <td className="mono">•••• {item.last4}</td>
                      <td>{item.description}</td>
                      <td className="numeric">{formatMoney(item.amount)}</td>
                      <td>
                        <div className="accounting-cell">
                          <AccountingCell item={item} items={options.data?.items} />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="actions">
          <button type="button" onClick={onClose}>
            Chiudi
          </button>
        </div>
      </div>
      {opened && (
        <AccountingDialog item={opened} canWrite={canWrite} onClose={() => setOpened(null)} />
      )}
    </dialog>
  );
}
